
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ExternalAuthParams struct
 */
class ExternalAuthParams {
  /**
   * Initialize struct
   * @param {!string=} mAEndpointHost
   * @param {!number=} mAEndpointPort
   * @constructor
   */
  constructor (mAEndpointHost = '', mAEndpointPort = 0) {
    this.MAEndpointHost = mAEndpointHost
    this.MAEndpointPort = mAEndpointPort
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ExternalAuthParams}
   * @param {!ExternalAuthParams} other Other struct
   * @returns {!ExternalAuthParams} This struct
   */
  copy (other) {
    if (other.MAEndpointHost != null) {
      this.MAEndpointHost = other.MAEndpointHost
    } else {
      this.MAEndpointHost = null
    }
    if (other.MAEndpointPort != null) {
      this.MAEndpointPort = other.MAEndpointPort
    } else {
      this.MAEndpointPort = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ExternalAuthParams}
   * @returns {!ExternalAuthParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ExternalAuthParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ExternalAuthParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ExternalAuthParams}
   * @param {!ExternalAuthParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ExternalAuthParams)) {
      throw new TypeError('Instance of ExternalAuthParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ExternalAuthParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      MAEndpointHost: ((this.MAEndpointHost != null) ? this.MAEndpointHost : null),
      MAEndpointPort: ((this.MAEndpointPort != null) ? this.MAEndpointPort : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ExternalAuthParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ExternalAuthParams} other Object value
   * @returns {!ExternalAuthParams} Created struct
   */
  static fromObject (other) {
    return new ExternalAuthParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ExternalAuthParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ExternalAuthParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ExternalAuthParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 94
  }
}

export { ExternalAuthParams };

/**
 * Fast Binary Encoding ExternalAuthParams field model
 */
class FieldModelExternalAuthParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.MAEndpointHost = new fbe.FieldModelString(buffer, 4 + 4)
    this.MAEndpointPort = new fbe.FieldModelUInt16(buffer, this.MAEndpointHost.fbeOffset + this.MAEndpointHost.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.MAEndpointHost.fbeSize + this.MAEndpointPort.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.MAEndpointHost.fbeExtra + this.MAEndpointPort.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelExternalAuthParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 94
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelExternalAuthParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelExternalAuthParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.MAEndpointHost.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MAEndpointHost.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MAEndpointHost.fbeSize

    if ((fbeCurrentSize + this.MAEndpointPort.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MAEndpointPort.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MAEndpointPort.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelExternalAuthParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelExternalAuthParams}
   * @param {!ExternalAuthParams} fbeValue Default value, defaults is new ExternalAuthParams()
   * @returns {!ExternalAuthParams} ExternalAuthParams value
   */
  get (fbeValue = new ExternalAuthParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelExternalAuthParams}
   * @param {!ExternalAuthParams} fbeValue ExternalAuthParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.MAEndpointHost.fbeSize) <= fbeStructSize) {
      fbeValue.MAEndpointHost = this.MAEndpointHost.get()
    } else {
      fbeValue.MAEndpointHost = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MAEndpointHost.fbeSize

    if ((fbeCurrentSize + this.MAEndpointPort.fbeSize) <= fbeStructSize) {
      fbeValue.MAEndpointPort = this.MAEndpointPort.get()
    } else {
      fbeValue.MAEndpointPort = 0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MAEndpointPort.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelExternalAuthParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelExternalAuthParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelExternalAuthParams}
   * @param {!ExternalAuthParams} fbeValue ExternalAuthParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelExternalAuthParams}
   * @param {ExternalAuthParams} fbeValue ExternalAuthParams value
   */
  setFields (fbeValue) {
    this.MAEndpointHost.set(fbeValue.MAEndpointHost)
    this.MAEndpointPort.set(fbeValue.MAEndpointPort)
  }
}

export { FieldModelExternalAuthParams };

/**
 * Fast Binary Encoding ExternalAuthParams model
 */
class ExternalAuthParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelExternalAuthParams(this.buffer, 4)
  }

  /**
   * Get the ExternalAuthParams model
   * @this {!ExternalAuthParamsModel}
   * @returns {!FieldModelExternalAuthParams} model ExternalAuthParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ExternalAuthParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ExternalAuthParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ExternalAuthParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ExternalAuthParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelExternalAuthParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ExternalAuthParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ExternalAuthParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ExternalAuthParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ExternalAuthParamsModel}
   * @param {!ExternalAuthParams} value ExternalAuthParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ExternalAuthParamsModel}
   * @param {!ExternalAuthParams} value ExternalAuthParams value, defaults is new ExternalAuthParams()
   * @return {!object} Deserialized ExternalAuthParams value and its size
   */
  deserialize (value = new ExternalAuthParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ExternalAuthParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ExternalAuthParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ExternalAuthParamsModel}
   * @param {!number} prev Previous ExternalAuthParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ExternalAuthParamsModel };
